/**
 * The guard on /words/:lang (plan v2 step 1): a language that is unknown or not
 * playable yet goes to the top page, keeping the query. Kept apart from the
 * registry so the router imports a few lines, not every language's map: the
 * registry loads only when a /words page is opened.
 */
import type { RouteLocationNormalized } from 'vue-router'
import { legacyPageCode } from './pageCode'

export async function wordLanguageGuard(to: Pick<RouteLocationNormalized, 'params' | 'query'> & { path?: string }) {
  const { languageAtPage, legacyEditionCode } = await import('./languages')
  if (languageAtPage(to.params.lang)) return true
  // A first two-letter code goes to its three-letter one, keeping /join/<token> and the query:
  // an English edition's (/words/en-hi, job #856 -> #877), and every other language's (/words/cy, ask #60).
  const now = legacyEditionCode(to.params.lang) ?? legacyPageCode(to.params.lang)
  if (now) return { path: (to.path ?? `/words/${String(to.params.lang)}`).replace(`/words/${String(to.params.lang)}`, `/words/${now}`), query: to.query }
  return { path: '/words', query: to.query }
}

/**
 * The key App.vue gives the routed view. /words/cym -> /words/gle reuses the one WordCountView
 * (same route record), and that view reads its language once at setup, so without a key
 * the word list and save key stayed Welsh. Keyed by language it remounts; every other route
 * gets no key, which is what it had before.
 */
export function wordViewKey(route: Pick<RouteLocationNormalized, 'path' | 'params'>): string | undefined {
  const m = /^\/words\/([^/]+)/.exec(route.path)
  if (!m || m[1] === 'exits' || m[1] === 'join') return undefined
  return `words:${String(route.params.lang ?? m[1])}`
}

/** The key for Pin it's page (job #979): its edition, English included, so a move between editions remounts it and reads the new one at setup. */
export function pinViewKey(route: Pick<RouteLocationNormalized, 'name' | 'params'>): string | undefined {
  if (route.name === 'guess-pin') return 'pin:eng'
  if (route.name === 'guess-pin-edition') return `pin:${String(route.params.edition)}`
  return undefined
}
